import { useState } from "react";
import classNames from "classnames";
import Slider from "components/DesignSystem/Slider/Slider";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { EditPencil } from "components/icons/EditPencil";
import { Trash20 } from "components/icons/Trash20";
import { PlusIcon } from "components/icons/PlusIcon";
import { CaretDown } from "components/icons/CaretDown";
import { MONTH_OPTIONS } from "./constants";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { useToast } from "hooks/useToast";
import {
  useGetMonthlyAdjustmentBreakdownQuery,
  useDeleteAdjustmentMutation,
} from "store/apis/estimatedTax";
import { TaxAdjustment } from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";
import { AdjustmentFormModal } from "./AdjustmentFormModal";

type Props = {
  show: boolean;
  onClose: () => void;
  entityId: string;
  taxYear: string;
  selectedMonth: number | null;
  isAdmin: boolean;
};

export const AdjustmentSlider = ({
  show,
  onClose,
  entityId,
  taxYear,
  selectedMonth,
  isAdmin,
}: Props) => {
  const { alertToast, successToast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editingAdj, setEditingAdj] = useState<TaxAdjustment | null>(null);
  const [defaultType, setDefaultType] = useState<"ADD_BACK" | "DEDUCTION">(
    "ADD_BACK"
  );
  const [openSections, setOpenSections] = useState({
    addBacks: true,
    deductions: true,
  });

  const { data: monthlyBreakdown = [], isLoading } =
    useGetMonthlyAdjustmentBreakdownQuery(
      { entityId, taxYear },
      { skip: !show }
    );

  const [deleteAdj, { isLoading: isDeleting }] = useDeleteAdjustmentMutation();

  const entry =
    selectedMonth != null
      ? monthlyBreakdown.find(
          (m) => m.month === MONTH_OPTIONS[selectedMonth - 1]?.value
        )
      : null;

  const addBacks = entry?.add_backs ?? [];
  const deductions = entry?.deductions ?? [];

  const openAdd = (type: "ADD_BACK" | "DEDUCTION") => {
    setEditingAdj(null);
    setDefaultType(type);
    setFormOpen(true);
  };

  const openEdit = (adj: TaxAdjustment) => {
    setEditingAdj(adj);
    setFormOpen(true);
  };

  const handleDelete = async (adjId: string) => {
    try {
      await deleteAdj({ entityId, adjustmentId: adjId }).unwrap();
      successToast({ message: "Adjustment deleted" });
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to delete adjustment",
      });
    }
  };

  const toggleSection = (
    section: "addBacks" | "deductions",
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const renderAdjRow = (adj: TaxAdjustment) => (
    <div
      key={adj.id}
      className="t-flex t-items-center t-justify-between t-py-2.5 t-border-b t-border-neutral-10 last:t-border-b-0"
    >
      <div className="t-flex t-flex-col t-gap-1">
        <span className="t-text-body-sm t-text-text-100">{adj.title}</span>
        <Tag tagType="gray" icon={false} rounded size="small">
          {adj.is_one_time
            ? "One-time"
            : `Recurring from ${entry?.label ?? ""}`}
        </Tag>
      </div>
      <div className="t-flex t-items-center t-gap-2">
        <span className="t-font-medium t-text-text-100">
          <AmountSuperScript amount={adj.amount} />
        </span>
        {isAdmin && (
          <span className="t-flex t-items-center t-gap-0.5">
            <Button
              customType="ghost_icon"
              size="small"
              onClick={() => openEdit(adj)}
            >
              <EditPencil className="t-text-text-30" />
            </Button>
            <Button
              customType="ghost_icon"
              size="small"
              onClick={() => handleDelete(adj.id)}
              disabled={isDeleting}
            >
              <Trash20 />
            </Button>
          </span>
        )}
      </div>
    </div>
  );

  const renderSection = (
    title: string,
    items: TaxAdjustment[],
    type: "ADD_BACK" | "DEDUCTION",
    sectionKey: "addBacks" | "deductions"
  ) => {
    const isOpen = openSections[sectionKey];
    return (
      <div className="t-rounded-lg t-border t-border-neutral-10 t-overflow-hidden">
        <div
          className="t-flex t-items-center t-justify-between t-px-4 t-py-3 t-bg-surface-lighter-grey t-cursor-pointer"
          onClick={(e) => toggleSection(sectionKey, e)}
        >
          <div className="t-flex t-items-center t-gap-2">
            <CaretDown
              className={classNames(
                "t-text-text-30 t-transition-transform t-duration-200",
                { "t-rotate-180": !isOpen }
              )}
            />
            <span className="t-font-semibold t-text-text-100">{title}</span>
            {items.length > 0 && (
              <span className="t-text-caption t-font-medium t-bg-neutral-10 t-text-text-60 t-rounded-full t-px-1.5 t-py-0.5">
                {items.length}
              </span>
            )}
          </div>
          {isAdmin && (
            <Button
              customType="ghost_icon"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openAdd(type);
              }}
            >
              <PlusIcon />
            </Button>
          )}
        </div>

        {isOpen && (
          <div className="t-px-4">
            {items.length === 0 ? (
              <p className="t-text-body-sm t-text-text-30 t-py-3">
                No {title.toLowerCase()} for this month.
              </p>
            ) : (
              items.map(renderAdjRow)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Slider.Root open={show} onOpenChange={(o) => !o && onClose()}>
        <Slider.Content width={420}>
          <Slider.Header>
            <Slider.Title>Adjustments — {entry?.label ?? ""}</Slider.Title>
            <Slider.Close />
          </Slider.Header>
          <Slider.Body className="t-flex t-flex-col t-gap-4">
            {isLoading ? (
              <div className="t-flex t-justify-center t-py-8">
                <Spinner size="small" />
              </div>
            ) : (
              <>
                {renderSection("Add-backs", addBacks, "ADD_BACK", "addBacks")}
                {renderSection(
                  "Deductions",
                  deductions,
                  "DEDUCTION",
                  "deductions"
                )}
              </>
            )}
          </Slider.Body>
        </Slider.Content>
      </Slider.Root>

      <AdjustmentFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        entityId={entityId}
        taxYear={taxYear}
        adjustment={editingAdj}
        defaultType={defaultType}
        defaultMonth={selectedMonth}
      />
    </>
  );
};
